import "./NodeItem.css";

import {
  ApplicationIcon,
  Badge,
  Box,
  ButtonIcon,
  Icon,
  Menu,
} from "@saviynt/design-system";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";

import { type CustomNodeTypes, type NodeData } from "../../types";
import { IconMap } from "../IconMap";
import { TextWrapper } from "../TextWrapper";
import { getApplicationSourceIcon } from "../../../PoliciesSidepanel/PoliciesSidepanel";

interface NodeItemProps {
  data: NodeData;
  isLeftNode: boolean;
  type: CustomNodeTypes;
  hideMenu?: boolean;
}

const initialOptionsWithIcons = [
  {
    label: "Make the focus",
    value: "make_the_focus",
    icon: "LocationOutline",
  },
];

export default function NodeItem({
  data,
  isLeftNode = false,
  type,
  hideMenu = false,
}: NodeItemProps) {
  const intl = useIntl();

  const {
    policyViolationCount,
    type: resourceType,
    displayName,
    onNodeClick,
    source,
  } = data;

  const isNonClickableNode = true;
  // TODO REMOVE COMMENT WHEN SERACH IS ENABLED
  //  ['Owners', 'Users', 'Entitlements'].includes(type);

  const iconElement = useMemo(
    () =>
      type !== "Owners" ? (
        <ButtonIcon
          className="icon"
          size="medium"
          kind="outlinedSecondary"
          icon={<IconMap source={type} size="xSmall" />}
          ariaLabel={`${type} icon`}
        />
      ) : null,
    [type],
  );

  const textElement =
    type === "Owners"
      ? data.label.split(" ").reduce((a, c) => a + c[0], "")
      : null;

  const handleChange = useCallback(() => {
    const { onMakeItFocusedNodeClick, ...rest } = data;

    onMakeItFocusedNodeClick?.(rest);
  }, [data]);

  let menuElement = useMemo(
    () =>
      hideMenu || isNonClickableNode ? (
        <span />
      ) : (
        <Box className="hidden-node-menu">
          <Menu
            options={initialOptionsWithIcons}
            onChange={handleChange}
            trigger={
              <ButtonIcon
                size="small"
                kind="outlinedSecondary"
                icon={<Icon kind="MoreVertical" size="xSmall" />}
                ariaLabel={intl.formatMessage({
                  defaultMessage: "More Items",
                  id: "/ACjSu",
                  description: "More Items",
                })}
              />
            }
          />
        </Box>
      ),
    [handleChange, hideMenu, intl, isNonClickableNode],
  );

  /* TODO REMOVE HIDE POST PM DISCUSSION */
  menuElement = null as unknown as React.JSX.Element;

  const badgeElement = useMemo(
    () =>
      policyViolationCount ? (
        <Badge
          kind="number"
          className="riskCount-badge"
          number={policyViolationCount}
          color="error"
          size="small"
        />
      ) : null,
    [policyViolationCount],
  );

  const sourceElement = useMemo(
    () =>
      source &&
      !["user", "owner", "entitlement"].includes(data.label?.toLowerCase()) ? (
        <div className="source-badge">
          <ApplicationIcon
            logo={getApplicationSourceIcon(source)}
            size="xxSmall"
          />
        </div>
      ) : null,
    [source, data.label],
  );

  const iconOrTextElement = useMemo(
    () => (
      <div
        className={`icon-wrapper ${policyViolationCount ? "error-icon" : ""}`}
      >
        {iconElement ?? textElement}
        {badgeElement}
        {sourceElement}
      </div>
    ),
    [
      policyViolationCount,
      iconElement,
      textElement,
      badgeElement,
      sourceElement,
    ],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onNodeClick?.(data);
    }
  };

  return (
    <div
      className={`Nhi-AccessMap-node-element ${
        !isNonClickableNode ? "Nhi-AccessMap-node-element-clickable" : ""
      }`}
      onClick={() => onNodeClick?.(data)}
      onKeyDown={handleKeyDown}
      role={!isNonClickableNode ? "button" : "presentation"}
      tabIndex={!isNonClickableNode ? 0 : -1}
      aria-label={
        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        !isNonClickableNode ? `${displayName} (${resourceType})` : undefined
      }
    >
      {isLeftNode ? menuElement : iconOrTextElement}

      <TextWrapper label={displayName} subLabel={resourceType} />

      {isLeftNode ? iconOrTextElement : menuElement}
    </div>
  );
}
